import {
  DeliveryDatesApiResponse,
  DeliveryDatesFetchState,
} from '@app/pages/api/delivery-dates/types';
import { useCallback, useRef, useState } from 'react';
import type { CartCheckoutClient } from '../../cartCheckoutClient';
import { ErrorCodeType } from '../types';
import {
  AsyncHookState,
  AsyncRequestResult,
  StateCode,
} from './AsyncHookState';

type GetDeliveryDatesResult = HookState & {
  data?: DeliveryDatesApiResponse;
};
type HookState = AsyncHookState<ErrorCodeType>;
type AsyncResult = AsyncRequestResult<ErrorCodeType>;

export const useGetDeliveryDates = ({
  client,
}: {
  client: CartCheckoutClient;
}) => {
  const abortController = useRef<AbortController>();
  const [result, setResult] = useState<GetDeliveryDatesResult>({});

  const handler = useCallback(
    async ({
      countryCode,
      productTypeIds,
      deliveryDatesFetchState,
    }: {
      countryCode: string;
      productTypeIds: string[];
      deliveryDatesFetchState: DeliveryDatesFetchState;
    }): Promise<AsyncResult> => {
      if (abortController.current) {
        abortController.current.abort();
      }

      const newAbortController = new AbortController();
      abortController.current = newAbortController;

      setResult((prev) => ({ ...prev, state: StateCode.LOADING }));

      // Note that fetchDeliveryDates() never throws. It always returns data, even
      // if that data is the fallback dates when the real data failed to be retrieved.
      return client
        .fetchDeliveryDates({
          countryCode,
          productTypeIds,
          fetchState: deliveryDatesFetchState,
        })
        .then((data) => {
          setResult({
            data,
            state: StateCode.SUCCESS,
          });
          return { state: StateCode.SUCCESS };
        });
    },
    [client]
  );

  return { ...result, abortController, handler };
};
